/**
 * Purchase Order Management Page
 * View and manage purchase orders, including setting expected shipment dates for line items.
 *
 * The `mark-delivered` flow patches the page from the API response — see
 * docs/REACTIVE_MUTATIONS.md. The initial "Loading purchase order…"
 * placeholder is reserved for the first fetch and route changes; a
 * successful mark-delivered submit must never flip the page back into
 * that state.
 */
import { Button, Group, Paper, Text } from '@mantine/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import WorkspacePage from '../components/landing/WorkspacePage';
import {
  AddPurchaseOrderLinePayload,
  OrderingAdapter,
  OrderPadExport,
  purchaseOrderAPI,
  PurchaseOrderFreightTerms,
  PurchaseOrderLineCandidate,
  PurchaseOrderPaymentSchedule,
  PurchaseOrderPaymentTerms,
  PurchaseOrderPriority,
  serializedComponentsAPI,
  SerializedTrackingMode,
  sigAPI,
  workOrderAPI,
} from '../services/api';
import { SIG, WorkOrder } from '../types';
import '../styles/PurchaseOrderPage.css';
import {
  OwningGroupIdentity,
  workOrderDetailsLabel,
  WorkOrderIdentity,
  workOrderOptionLabel,
} from '../utils/associations';
import { formatDateOnly, formatYmd, utcYmd, ymdToUtcDateTime } from '../utils/dates';
import { confirmAction, promptInput, showError, showSuccess } from '../utils/dialogs';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import {
  freightTermsLabel,
  paymentScheduleSummary,
  paymentTermsLabel,
  PO_FREIGHT_TERMS_OPTIONS,
  PO_PAYMENT_TERMS_OPTIONS,
  PO_PRIORITY_OPTIONS,
  priorityLabel,
} from '../utils/purchaseOrderTerms';
import { parseSerialNumbers } from '../utils/serializedComponents';

/** One line of a receive request, as the API takes it. */
interface ReceiveLine {
  purchase_order_item: number;
  quantity_received: number;
  serials?: { serial_number: string; item?: string; lot?: string; expiration_date?: string }[];
  close_short?: boolean;
  close_short_reason?: string;
}

/** A line's position in the receiving workflow, mirroring the server's choices. */
type ReceiptState =
  | 'not_received'
  | 'partially_received'
  | 'received'
  | 'over_received'
  | 'closed_short'
  | 'voided';

/** An inventory identity a receipt may record serials against. */
interface SerialTarget {
  item: string;
  item_name: string;
  item_sku: string;
  serial_tracking_mode?: SerializedTrackingMode;
  /** Units of this identity the FULL ordered quantity implies. */
  quantity: number;
}

interface PurchaseOrderItem {
  id: string;
  item_type: 'inventory_item' | 'asset' | 'freeform' | null;
  description: string | null;
  item_details: {
    id: string;
    name: string;
    sku: string;
    is_serialized?: boolean;
    serial_tracking_mode?: SerializedTrackingMode;
  } | null;
  asset_details: {
    id: string;
    name: string;
    asset_tag: string;
    location_name: string | null;
  } | null;
  // Kit lines (op-8n0). Both come from the LINE payload; the breakdown is
  // never fetched live from the kit, because what matters when receiving is
  // what this line will credit.
  is_kit_line?: boolean;
  kit_components?: Array<{
    component: string;
    component_name: string;
    component_sku: string;
    quantity_per_kit: number;
    quantity: number;
  }> | null;
  quantity_ordered: number;
  quantity_received: number;
  quantity_pending: number;
  is_fully_received: boolean;
  // Receiving state (oms-po-receiving), all derived server-side so the badge
  // here and the API's own answer cannot disagree. `quantity_variance` is
  // signed — negative short, positive over — and is the figure
  // `quantity_pending` deliberately floors away.
  quantity_variance: number;
  receipt_state: ReceiptState;
  receipt_state_label: string;
  is_settled: boolean;
  has_receipt_variance: boolean;
  is_over_received: boolean;
  is_short_received: boolean;
  is_closed_short: boolean;
  closed_short_at: string | null;
  closed_short_by_username: string | null;
  closed_short_reason: string;
  // Which identities a receipt on this line may carry serials for — the kit's
  // COMPONENTS on a kit line, never the kit. Empty when nothing here is
  // serialized. Never inferred from `item_details.is_serialized`: on a kit
  // line that field describes the kit, which is never stocked.
  serial_targets: SerialTarget[];
  serials_recorded: number;
  // Units of a serialized identity already on the shelf with no serial
  // recorded. Non-zero means real outstanding work — this is what replaced
  // the old ban on serialized kit components, and it covers every receive
  // path including `mark-delivered`, which records no serials at all.
  serials_outstanding: number;
  unit_cost_ordered: string;
  unit_cost_actual: string | null;
  estimated_cost: string;
  actual_cost: string | null;
  expected_shipment_date: string | null;
  notes: string;
  is_voided: boolean;
  voided_at: string | null;
  void_reason: string;
  // Who this line was bought for (op-bu80 / op-shb9). Both editable here.
  work_order: string | null;
  work_order_details: WorkOrderIdentity | null;
  owning_group: number | null;
  owning_group_details: OwningGroupIdentity | null;
}

interface PurchaseOrderAttachment {
  id: number;
  file: string;
  file_url: string | null;
  file_name: string | null;
  description: string;
  uploaded_by: number | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

interface PurchaseOrder {
  id: string;
  po_number: string;
  supplier_details: string;
  // Selects the adapter-aware order-pad affordances (op-svpq): Amazon "Open
  // cart" vs HD Supply / generic download+copy. Read-only, from the supplier.
  supplier_ordering_adapter: OrderingAdapter | null;
  // The purchase/pricing agreement this order was placed under (op-yoos).
  // Optional — most orders are placed at list price.
  supplier_agreement: number | null;
  supplier_agreement_details: { id: number; name: string } | null;
  // Who the whole order was placed for (op-shb9). Attribution only — the
  // material bridge and the receiving ledger read the lines, not these.
  work_order: string | null;
  work_order_details: WorkOrderIdentity | null;
  owning_group: number | null;
  owning_group_details: OwningGroupIdentity | null;
  status: string;
  status_label: string;
  // Receiving roll-up (oms-po-receiving). `is_settled` is "receiving is
  // finished with this order"; `is_fully_received` stays the stricter
  // "everything we ordered turned up". They differ exactly when a line was
  // closed short, and `has_receipt_variance` keeps that visible afterwards.
  is_settled: boolean;
  has_receipt_variance: boolean;
  outstanding_line_count: number;
  // Units taken in across every line. Zero means nothing ever arrived, which
  // is why an order can be fully settled and still not read `received`.
  total_received_quantity: number;
  variance_line_count: number;
  serials_outstanding: number;
  // Served by the API from its own RECEIVABLE_STATUSES, so the button and the
  // endpoint cannot disagree about whether this order can be received against.
  can_receive: boolean;
  // Served by the API from its own PRE_SUPPLIER_STATUSES — the same discipline,
  // and for a sharper reason: this flag decides whether a line's action is an
  // irreversible DESTROY or a recorded void. Never re-derive it from `status`.
  can_delete_items: boolean;
  // Header terms (op-bwo9), all editable here. `order_date` is a datetime that
  // carries a business *day* — the server derives `payment_schedule` from its
  // UTC date — so it is read and written as a day (see `utcYmd`).
  order_date: string;
  priority: PurchaseOrderPriority;
  payment_terms: PurchaseOrderPaymentTerms | '';
  freight_terms: PurchaseOrderFreightTerms | '';
  // Derived, read-only: the single payment these terms imply.
  payment_schedule: PurchaseOrderPaymentSchedule | null;
  expected_delivery_date: string | null;
  supplier_order_number: string;
  sales_order_number: string;
  attachments: PurchaseOrderAttachment[];
  items: PurchaseOrderItem[];
  estimated_total: string;
  voided_at: string | null;
  voided_by_username: string | null;
  void_reason: string;
}

/**
 * What KIND of thing a line orders (op-49th) — deliberately distinct from
 * op-shb9's "Ordered For" column, which is the job/committee the line was
 * bought *for*.
 *
 * Caveat: a line raised from a reorder request is indistinguishable from a
 * hand-added inventory line on the wire (the serializer carries no reorder FK),
 * so it reads as "Inventory item" here. A real "Reorder" badge needs a backend
 * `reorder_request` field on the line first.
 */
const ITEM_TYPE_LABELS: Record<NonNullable<PurchaseOrderItem['item_type']>, string> = {
  inventory_item: 'Inventory item',
  asset: 'Asset',
  freeform: 'Freeform',
};

const getItemTypeLabel = (item: PurchaseOrderItem): string =>
  (item.item_type && ITEM_TYPE_LABELS[item.item_type]) || '—';

const getItemNameAndSku = (item: PurchaseOrderItem): { itemName: string; itemSku: string } => {
  if (item.item_type === 'asset') {
    return {
      itemName: item.asset_details?.name || 'Unknown Asset',
      itemSku: item.asset_details?.asset_tag || '—',
    };
  }
  if (item.item_type === 'freeform') {
    return { itemName: item.description || 'Unknown Item', itemSku: '—' };
  }
  return {
    itemName: item.item_details?.name || 'Unknown Item',
    itemSku: item.item_details?.sku || '—',
  };
};

/**
 * Money for prose rather than for a table cell — the delete confirmation has
 * to name what it is about to destroy, and it is built before the component's
 * own `formatCurrency` is in scope. Falls back to the raw string rather than
 * to an em dash: a confirmation that says "—" where the cost should be tells
 * the operator less than the unformatted number does.
 */
const money = (value: string | null): string => {
  if (!value) return '$0.00';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(num);
};

/**
 * The two independent add-line controls on this page: the scan-and-Enter
 * identifier field, and the "Add a custom line" freeform disclosure under it.
 */
type AddLineControl = 'identifier' | 'custom';

/**
 * The three states an add-line control can be put into. Every exit path of the
 * add-line handlers names one of these rather than deciding for itself which
 * of the errors, notices, fields and candidates to touch.
 */
type AddLineFormState =
  | { kind: 'pending' }
  | { kind: 'refused'; message: string; candidates?: PurchaseOrderLineCandidate[] }
  | { kind: 'added'; notice: string };

interface SelectOption {
  value: string;
  label: string;
}

/**
 * Options for an association picker, with the currently-attached target kept
 * selectable even when it is not in the fetched list (op-shb9) — the pickers
 * offer open/in-progress jobs and the viewer's own committees, so an order
 * tagged with a finished job or another committee's SIG would otherwise show
 * blank and be silently cleared by an unrelated edit.
 */
const withCurrentOption = (
  available: SelectOption[],
  currentValue: string,
  currentLabel: string,
): SelectOption[] =>
  currentValue && !available.some((option) => option.value === currentValue)
    ? [{ value: currentValue, label: currentLabel }, ...available]
    : available;

/**
 * A per-line state map without `key`, or the same map when it never held one.
 *
 * Returning the original object on a miss keeps a no-op edit from re-rendering,
 * and dropping rather than falsifying the entry means "the operator did not say
 * this" is stored as absence, exactly as it was before they said it.
 */
const dropKey = <T,>(map: Record<string, T>, key: string): Record<string, T> => {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
};

/** The work-order + committee picker pair, used at order and line level. */
const AssociationPickers: React.FC<{
  idPrefix: string;
  workOrderOptions: SelectOption[];
  committeeOptions: SelectOption[];
  workOrderValue: string;
  committeeValue: string;
  onWorkOrderChange: (value: string) => void;
  onCommitteeChange: (value: string) => void;
  disabled?: boolean;
}> = ({
  idPrefix,
  workOrderOptions,
  committeeOptions,
  workOrderValue,
  committeeValue,
  onWorkOrderChange,
  onCommitteeChange,
  disabled,
}) => (
  <>
    <label htmlFor={`${idPrefix}-work-order`}>
      Work Order
      <select
        id={`${idPrefix}-work-order`}
        value={workOrderValue}
        onChange={(e) => onWorkOrderChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">No work order</option>
        {workOrderOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
    <label htmlFor={`${idPrefix}-committee`}>
      Committee
      <select
        id={`${idPrefix}-committee`}
        value={committeeValue}
        onChange={(e) => onCommitteeChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">No committee</option>
        {committeeOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  </>
);

const PurchaseOrderPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [shipmentDate, setShipmentDate] = useState<string>('');
  const [editingCostItemId, setEditingCostItemId] = useState<string | null>(null);
  const [lineCost, setLineCost] = useState<string>('');
  const [saving, setSaving] = useState(false);
  // Dedicated in-flight flag for the draft→sent / sent→confirmed hero
  // transitions so their disabled state is independent of the line-item
  // `saving` flag used elsewhere on the page.
  const [transitioning, setTransitioning] = useState(false);
  const [voidingItemId, setVoidingItemId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [deliveryTracking, setDeliveryTracking] = useState<string>('');
  const [deliveryCarrier, setDeliveryCarrier] = useState<string>('');
  const [receivingItems, setReceivingItems] = useState(false);
  const [receiveQuantities, setReceiveQuantities] = useState<Record<string, string>>({});
  // Per-serialized-line captured serial numbers (one per line / comma-separated),
  // keyed by purchase-order line id.
  // Keyed by `serialKey(line, target)` rather than by line id: one kit line
  // can credit several serialized identities, and a single box per line would
  // make the operator's cartridge serials and meter serials indistinguishable.
  const [serialInputs, setSerialInputs] = useState<Record<string, string>>({});
  const [serialLots, setSerialLots] = useState<Record<string, string>>({});
  const [serialExpiries, setSerialExpiries] = useState<Record<string, string>>({});
  const [receiveDeliveryDate, setReceiveDeliveryDate] = useState<string>('');
  const [receiveNotes, setReceiveNotes] = useState<string>('');
  // The carrier's tracking barcode for the parcel being unpacked. The receive
  // endpoint has always accepted this; the form did not collect it, so it was
  // only ever recorded by the separate mark-delivered path.
  const [receiveTracking, setReceiveTracking] = useState<string>('');
  const [receiveCarrier, setReceiveCarrier] = useState<string>('');
  const [closeShortLines, setCloseShortLines] = useState<Record<string, boolean>>({});
  const [closeShortReasons, setCloseShortReasons] = useState<Record<string, string>>({});
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [metadataSupplierOrderNumber, setMetadataSupplierOrderNumber] = useState('');
  const [metadataSalesOrderNumber, setMetadataSalesOrderNumber] = useState('');
  const [metadataExpectedDelivery, setMetadataExpectedDelivery] = useState('');
  // Header terms (op-bwo9), edited alongside the rest of the order details.
  // `metadataOrderDate` is a 'YYYY-MM-DD' day; empty terms mean "not agreed".
  const [metadataOrderDate, setMetadataOrderDate] = useState('');
  const [metadataPriority, setMetadataPriority] = useState<PurchaseOrderPriority>('normal');
  const [metadataPaymentTerms, setMetadataPaymentTerms] = useState<PurchaseOrderPaymentTerms | ''>(
    '',
  );
  const [metadataFreightTerms, setMetadataFreightTerms] = useState<PurchaseOrderFreightTerms | ''>(
    '',
  );
  // Order-level associations (op-shb9), edited alongside the other order
  // metadata. Empty string means "no association" in both pickers.
  const [metadataWorkOrder, setMetadataWorkOrder] = useState('');
  const [metadataCommittee, setMetadataCommittee] = useState('');
  // Per-line associations. `editingAssociationItemId` is the line whose cell is
  // currently a pair of pickers; the two draft values back those pickers.
  const [editingAssociationItemId, setEditingAssociationItemId] = useState<string | null>(null);
  const [lineWorkOrder, setLineWorkOrder] = useState('');
  const [lineCommittee, setLineCommittee] = useState('');
  // Options for every association picker on this page.
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [sigs, setSigs] = useState<SIG[]>([]);
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  // Order export (adapter-aware, op-svpq) — last-fetched payload drives the
  // missing-/invalid-SKU warnings and the Amazon multi-cart panel;
  // `exportingOrderPad` disables the buttons while a fetch is in flight.
  const [orderPad, setOrderPad] = useState<OrderPadExport | null>(null);
  const [exportingOrderPad, setExportingOrderPad] = useState(false);
  // Add-a-line entry (oms-po-add-item). One field takes whatever the operator
  // types or the scanner emits — item name, item SKU, package or unit barcode,
  // or the vendor's SKU — and Enter submits it, so a scan (a fast burst of
  // characters ending in Enter) completes the add without touching the mouse.
  // `addLineCandidates` is populated only when the server refuses to guess
  // between several matches; `addLineNotice` reports what actually matched.
  const [addLineIdentifier, setAddLineIdentifier] = useState('');
  const [addingLine, setAddingLine] = useState(false);
  const [addLineError, setAddLineError] = useState<string | null>(null);
  const [addLineCandidates, setAddLineCandidates] = useState<PurchaseOrderLineCandidate[]>([]);
  const [addLineNotice, setAddLineNotice] = useState<string | null>(null);
  // The freeform half of the same control. A purchase order has always been
  // able to carry a line the catalogue does not know about — a one-off freight
  // charge, a part bought once and never stocked — but only at create time, so
  // remembering one late still meant retyping the order. Kept behind a
  // disclosure so the scan-and-Enter path above stays the obvious one: this is
  // the exception, not the workflow.
  const [showCustomLine, setShowCustomLine] = useState(false);
  const [customLineDescription, setCustomLineDescription] = useState('');
  const [customLineUnitCost, setCustomLineUnitCost] = useState('');
  const [customLineQuantity, setCustomLineQuantity] = useState('1');
  // Its own refusal and confirmation slots. The identifier form's messages sit
  // inline under that field, which is the wrong place to answer a custom line
  // the operator is still looking at further down the page — and a confirmation
  // routed to the wrong form is worse than a refusal routed to the wrong form,
  // because the operator has no reason to go looking for it.
  const [customLineError, setCustomLineError] = useState<string | null>(null);
  const [customLineNotice, setCustomLineNotice] = useState<string | null>(null);
  // The scanner loop has to stay mouse-free, so the caret goes back into the
  // entry field the moment an add settles — after a scan-and-Enter, and after a
  // click on one of the ambiguity candidates, whose button disappears with the
  // list that held it. Done in an effect rather than in `submitAddLine` so it
  // runs after the re-render that re-enables the controls.
  //
  // `select()`, not a bare `focus()`: a refusal deliberately leaves the typed
  // text in place so the operator can correct it, and a scanner delivers a
  // burst plus an Enter. Appending that burst onto the old text would turn the
  // next scan into a bogus identifier and a guaranteed second refusal, so the
  // text stays visible but the next scan overwrites it.
  //
  // Only for adds that came from that field. A custom line is submitted from a
  // form further down the page, and pulling the caret — and the viewport, since
  // focus scrolls — back up to the scan field would drag the operator away from
  // the control they are still working in.
  const addLineInputRef = useRef<HTMLInputElement>(null);
  const addLineWasInFlight = useRef(false);
  const addLineCameFromScanField = useRef(true);
  useEffect(() => {
    if (addLineWasInFlight.current && !addingLine && addLineCameFromScanField.current) {
      addLineInputRef.current?.focus();
      addLineInputRef.current?.select();
    }
    addLineWasInFlight.current = addingLine;
  }, [addingLine]);

  // First scan of the session needs no mouse either, so the field takes focus
  // as soon as the control exists. `preventScroll` rather than the `autoFocus`
  // attribute: the control sits below the header, details and attachments, so
  // letting the browser scroll it into view would land every draft order —
  // including one opened just to check its supplier or dates — at Line Items.
  const canAddLine = isAuthenticated && order?.status === 'draft';
  useEffect(() => {
    if (canAddLine) {
      addLineInputRef.current?.focus({ preventScroll: true });
    }
  }, [canAddLine]);

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchaseOrderAPI.getOrder(orderId!);
      setOrder(response.data);
    } catch (err: any) {
      console.error('Error loading purchase order:', err);
      setError(extractErrorMessage(err, 'Failed to load purchase order'));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    setIsStaff(
      localStorage.getItem('is_staff') === 'true' ||
        localStorage.getItem('is_superuser') === 'true',
    );

    if (orderId) {
      loadOrder();
    }
  }, [orderId, loadOrder]);

  // Association picker options (op-shb9). Only fetched for signed-in users —
  // the page is publicly readable, and an anonymous visitor edits nothing.
  // Neither list is required to read the order, so a failure just empties that
  // picker instead of erroring the page.
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    (async () => {
      const [openWos, activeWos, mySigs] = await Promise.allSettled([
        workOrderAPI.listWorkOrders({ status: 'open' }),
        workOrderAPI.listWorkOrders({ status: 'in_progress' }),
        sigAPI.listMySIGs(),
      ]);
      if (cancelled) return;
      const results = (settled: PromiseSettledResult<any>): WorkOrder[] =>
        settled.status === 'fulfilled' ? (settled.value?.data?.results ?? []) : [];
      setWorkOrders([...results(openWos), ...results(activeWos)]);
      setSigs(mySigs.status === 'fulfilled' ? (mySigs.value?.data?.results ?? []) : []);
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Base option lists for every association picker on the page. The
  // currently-attached target is grafted on per picker (see withCurrentOption).
  const workOrderOptions: SelectOption[] = workOrders.map((workOrder) => ({
    value: workOrder.id,
    label: workOrderOptionLabel(workOrder),
  }));
  const committeeOptions: SelectOption[] = sigs.map((sig) => ({
    value: String(sig.id),
    label: sig.name,
  }));

  const handleEditShipmentDate = (item: PurchaseOrderItem) => {
    setEditingItemId(item.id);
    setShipmentDate(item.expected_shipment_date || '');
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setShipmentDate('');
  };

  const handleEditLineCost = (item: PurchaseOrderItem) => {
    setEditingCostItemId(item.id);
    // Calculate line cost from unit_cost_actual if available, otherwise from estimated_cost
    if (item.unit_cost_actual && item.quantity_received > 0) {
      const calculatedLineCost = parseFloat(item.unit_cost_actual) * item.quantity_received;
      setLineCost(calculatedLineCost.toFixed(2));
    } else if (item.actual_cost) {
      setLineCost(parseFloat(item.actual_cost).toFixed(2));
    } else {
      // Use estimated cost as starting point
      setLineCost(parseFloat(item.estimated_cost).toFixed(2));
    }
  };

  const handleCancelEditCost = () => {
    setEditingCostItemId(null);
    setLineCost('');
  };

  const handleSaveLineCost = async (itemId: string, item: PurchaseOrderItem) => {
    const lineCostValue = parseFloat(lineCost);
    if (isNaN(lineCostValue) || lineCostValue < 0) {
      showError('Please enter a valid line cost (must be a positive number)');
      return;
    }

    try {
      setSaving(true);
      await purchaseOrderAPI.updateLineItem(orderId!, itemId, {
        line_cost: lineCostValue,
      });
      await loadOrder(); // Reload to get updated data
      setEditingCostItemId(null);
      setLineCost('');
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to update line cost'));
      console.error('Error updating line cost:', err);
    } finally {
      setSaving(false);
    }
  };

  // Per-line associations (op-shb9): "this line is for job X / committee Y".
  // Set after the fact as often as at order time — which job the parts were for
  // is frequently identified once they arrive.
  const handleEditAssociation = (item: PurchaseOrderItem) => {
    setEditingAssociationItemId(item.id);
    setLineWorkOrder(item.work_order || '');
    setLineCommittee(item.owning_group ? String(item.owning_group) : '');
  };

  const handleCancelEditAssociation = () => {
    setEditingAssociationItemId(null);
    setLineWorkOrder('');
    setLineCommittee('');
  };

  const handleSaveAssociation = async (itemId: string) => {
    try {
      setSaving(true);
      await purchaseOrderAPI.updateLineItem(orderId!, itemId, {
        work_order: lineWorkOrder || null,
        owning_group: lineCommittee ? Number(lineCommittee) : null,
      });
      await loadOrder();
      handleCancelEditAssociation();
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to update line associations'));
      console.error('Error updating line associations:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveShipmentDate = async (itemId: string) => {
    try {
      setSaving(true);
      await purchaseOrderAPI.updateLineItem(orderId!, itemId, {
        expected_shipment_date: shipmentDate || undefined,
      });
      await loadOrder(); // Reload to get updated data
      setEditingItemId(null);
      setShipmentDate('');
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to update shipment date'));
      console.error('Error updating shipment date:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleVoidItem = (itemId: string) => {
    if (!voidReason.trim()) {
      showError('Please provide a reason for voiding this line item');
      return;
    }

    confirmAction(
      'Void line item?',
      'Are you sure you want to void this line item? This will also mark the item as discontinued from this supplier.',
      async () => {
        try {
          setSaving(true);
          await purchaseOrderAPI.voidLineItem(orderId!, itemId, voidReason);
          await loadOrder(); // Reload to get updated data
          setVoidingItemId(null);
          setVoidReason('');
        } catch (err: any) {
          showError(extractErrorMessage(err, 'Failed to void line item'));
          console.error('Error voiding line item:', err);
        } finally {
          setSaving(false);
        }
      },
      { labels: { confirm: 'Void', cancel: 'Cancel' }, color: 'red' },
    );
  };

  /**
   * DESTROY a line on a pre-send order.
   *
   * Deliberately unlike {@link handleVoidItem} at every step, because the two
   * must not be mistakable for one another: no reason field to fill in (the
   * friction that made voiding the wrong tool for a typo), and a confirmation
   * that NAMES the line and its quantity and cost rather than asking "are you
   * sure?" about an unspecified row. An operator who misreads the button
   * should still be stopped by the sentence in the dialog.
   */
  const handleDeleteItem = (item: PurchaseOrderItem) => {
    const { itemName } = getItemNameAndSku(item);
    confirmAction(
      'Delete this line?',
      `Permanently delete "${itemName}" — ${item.quantity_ordered} × ` +
        `${money(item.unit_cost_ordered)}, ${money(item.estimated_cost)} total — from this order. ` +
        'The line is removed entirely, leaving no record of it. This cannot be undone.',
      async () => {
        try {
          setSaving(true);
          const response = await purchaseOrderAPI.deleteLineItem(orderId!, item.id);
          // Patch in place from the mutation response rather than re-running
          // the initial loader (docs/REACTIVE_MUTATIONS.md).
          setOrder(response.data.purchase_order);
          showSuccess(`Deleted ${itemName} from this order`);
        } catch (err: any) {
          showError(extractErrorMessage(err, 'Failed to delete line item'));
          console.error('Error deleting line item:', err);
        } finally {
          setSaving(false);
        }
      },
      { labels: { confirm: 'Delete line', cancel: 'Keep it' }, color: 'red' },
    );
  };

  const handleVoidOrder = async () => {
    const reason = await promptInput(
      'Void purchase order',
      'Reason for voiding (optional)',
      undefined,
      { placeholder: 'e.g. supplier rejected all line items' },
    );
    if (reason === null) return;

    confirmAction(
      'Void this purchase order?',
      'This will void the PO and cascade to all non-voided line items. This cannot be undone.',
      async () => {
        try {
          setSaving(true);
          await purchaseOrderAPI.voidOrder(orderId!, reason);
          showSuccess('Purchase order voided');
          await loadOrder();
        } catch (err: any) {
          showError(extractErrorMessage(err, 'Failed to void purchase order'));
          console.error('Error voiding purchase order:', err);
        } finally {
          setSaving(false);
        }
      },
      { labels: { confirm: 'Void PO', cancel: 'Cancel' }, color: 'red' },
    );
  };

  const canVoidOrder = (po: PurchaseOrder) =>
    isStaff && po.status !== 'voided' && po.status !== 'received';

  const canSendToSupplier = (po: PurchaseOrder) => isAuthenticated && po.status === 'draft';

  const canConfirmOrder = (po: PurchaseOrder) => isAuthenticated && po.status === 'sent';

  const handleSendToSupplier = async () => {
    if (transitioning) return;
    try {
      setTransitioning(true);
      await purchaseOrderAPI.sendToSupplier(orderId!);
      await loadOrder();
      showSuccess('Purchase order sent to supplier');
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to send purchase order to supplier'));
      console.error('Error sending purchase order to supplier:', err);
    } finally {
      setTransitioning(false);
    }
  };

  const handleConfirmOrder = async () => {
    if (transitioning) return;
    try {
      setTransitioning(true);
      await purchaseOrderAPI.confirmOrder(orderId!);
      await loadOrder();
      showSuccess('Purchase order confirmed');
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to confirm purchase order'));
      console.error('Error confirming purchase order:', err);
    } finally {
      setTransitioning(false);
    }
  };

  // Fetch the adapter-appropriate order export for this PO (op-svpq). Shared by
  // every export affordance (CSV download, copy block, Amazon cart); also
  // refreshes the missing-/invalid-SKU warnings so the operator sees which lines
  // still need a usable supplier part number before they order.
  const fetchOrderPad = async (): Promise<OrderPadExport | null> => {
    try {
      setExportingOrderPad(true);
      const response = await purchaseOrderAPI.exportOrder(orderId!);
      setOrderPad(response.data);
      return response.data;
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to build order pad'));
      console.error('Error building order pad:', err);
      return null;
    } finally {
      setExportingOrderPad(false);
    }
  };

  const handleDownloadOrderPad = async () => {
    const pad = await fetchOrderPad();
    if (!pad || pad.csv == null) return;
    const blob = new Blob([pad.csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = pad.filename ?? 'order.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleCopyOrderPad = async () => {
    const pad = await fetchOrderPad();
    if (!pad || pad.text == null) return;
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(pad.text);
        showSuccess('Order pad copied to clipboard');
      } catch {
        showError('Could not copy order pad to clipboard');
      }
    } else {
      showError('Clipboard is not available in this browser');
    }
  };

  // Amazon adapter (op-svpq): the export returns add-to-cart URL(s) rather than a
  // file. Open the first cart in a new tab; when the PO is chunked across
  // several carts (Amazon caps cart-URL length) the per-chunk buttons rendered
  // below let the operator open each one.
  const openAmazonCart = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenAmazonCart = async () => {
    const pad = await fetchOrderPad();
    if (!pad) return;
    const urls = pad.cart_urls ?? [];
    if (urls.length === 0) {
      showError('No Amazon cart could be built — check the lines have valid ASINs.');
      return;
    }
    openAmazonCart(urls[0]);
  };

  const handleOpenMarkDelivered = () => {
    const today = formatYmd(new Date());
    setDeliveryDate(today);
    setDeliveryTracking('');
    setDeliveryCarrier('');
    setMarkingDelivered(true);
  };

  const handleCancelMarkDelivered = () => {
    setMarkingDelivered(false);
    setDeliveryDate('');
    setDeliveryTracking('');
    setDeliveryCarrier('');
  };

  const handleSubmitMarkDelivered = async () => {
    if (!deliveryDate) {
      showError('Please select a delivery date');
      return;
    }
    if (saving) return;

    try {
      setSaving(true);
      const response = await purchaseOrderAPI.markDelivered(orderId!, {
        delivery_date: deliveryDate,
        tracking_number: deliveryTracking || undefined,
        carrier: deliveryCarrier || undefined,
      });
      if (response.data && typeof response.data === 'object' && response.data.id) {
        setOrder(response.data as PurchaseOrder);
      }
      handleCancelMarkDelivered();
      showSuccess('Purchase order marked as delivered');
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to mark purchase order as delivered'));
      console.error('Error marking delivered:', err);
    } finally {
      setSaving(false);
    }
  };

  const canMarkDelivered = (po: PurchaseOrder) =>
    isAuthenticated && ['sent', 'confirmed', 'partially_received'].includes(po.status);

  // Served by the API from its own RECEIVABLE_STATUSES. The local copy of that
  // list this used to keep is how the button and the endpoint could disagree.
  const canReceiveItems = (po: PurchaseOrder) => isAuthenticated && po.can_receive;

  /**
   * Why receiving is unavailable on this order, or null when it is available.
   *
   * The receive affordances used to simply vanish on a PO whose status did not
   * allow receiving, which reads as a broken button rather than a finished or
   * not-yet-sent order. Naming the state the order is in — and what to do about
   * it — is the difference between "this app is broken" and "ah, send it
   * first". The statuses here are the complement of `canReceiveItems`, so the
   * two cannot disagree about when a button is offered.
   */
  const receiveUnavailableReason = (po: PurchaseOrder): string | null => {
    if (canReceiveItems(po)) return null;
    switch (po.status) {
      case 'draft':
        return 'This order is still a draft. Send it to the supplier before receiving against it.';
      case 'received':
        // Deliberately not always "received in full": an order reaches
        // `received` once receiving is FINISHED with every line, which
        // includes lines closed short. Claiming everything arrived would be a
        // documented untruth on exactly the orders most in need of chasing.
        return po.has_receipt_variance
          ? 'Receiving has finished with this order. Some lines did not match what was ordered — see the flags below.'
          : 'Every line on this order has already been received in full.';
      case 'cancelled':
        return 'This order was cancelled, so nothing can be received against it.';
      case 'voided':
        return 'This order was voided, so nothing can be received against it.';
      default:
        return `This order is ${po.status_label || po.status}, which cannot receive items.`;
    }
  };

  // Lines receiving is not finished with. `is_settled` rather than
  // `is_fully_received`: a line closed short is done with even though it never
  // got its full quantity, and re-offering it would invite a receipt the API
  // now refuses.
  const getReceivableItems = (po: PurchaseOrder) =>
    po.items.filter((item) => !item.is_voided && !item.is_settled);

  const handleOpenReceiveItems = () => {
    if (!order) return;
    const initialQuantities: Record<string, string> = {};
    getReceivableItems(order).forEach((item) => {
      initialQuantities[item.id] = String(item.quantity_pending);
    });
    setReceiveQuantities(initialQuantities);
    setSerialInputs({});
    setSerialLots({});
    setSerialExpiries({});
    setReceiveDeliveryDate(formatYmd(new Date()));
    setReceiveNotes('');
    setReceiveTracking('');
    setReceiveCarrier('');
    setCloseShortLines({});
    setCloseShortReasons({});
    setReceivingItems(true);
  };

  const handleCancelReceiveItems = () => {
    setReceivingItems(false);
    setReceiveQuantities({});
    setSerialInputs({});
    setSerialLots({});
    setSerialExpiries({});
    setReceiveDeliveryDate('');
    setReceiveNotes('');
    setReceiveTracking('');
    setReceiveCarrier('');
    setCloseShortLines({});
    setCloseShortReasons({});
  };

  const handleReceiveQuantityChange = (itemId: string, value: string) => {
    setReceiveQuantities((prev) => ({ ...prev, [itemId]: value }));

    // The close-short offer only exists while the quantity is short, so the
    // flag must not outlive it. Ticking "the rest is not coming" at 8 of 10 and
    // then correcting the quantity to 10 hides the row; keeping the flag would
    // send a close-short the operator can no longer see or untick — input they
    // never confirmed, against a line with nothing left outstanding.
    //
    // Only a COMPLETED edit clears it. An empty box, a zero, or anything that
    // does not parse is a quantity mid-edit — every backspace-and-retype passes
    // through one — and dropping the typed reason there would discard what the
    // operator entered while they were still entering it. Nothing invisible can
    // be submitted from those states either: `handleSubmitReceiveItems` skips a
    // line with no usable quantity before it ever reads the flag.
    const item = order?.items.find((candidate) => candidate.id === itemId);
    const parsed = Number.parseInt(value, 10);
    const noLongerShort =
      item !== undefined &&
      !Number.isNaN(parsed) &&
      parsed > 0 &&
      parsed >= item.quantity_pending;
    if (!noLongerShort) return;

    setCloseShortLines((prev) => dropKey(prev, itemId));
    setCloseShortReasons((prev) => dropKey(prev, itemId));
  };

  const handleSerialInputChange = (key: string, value: string) => {
    setSerialInputs((prev) => ({ ...prev, [key]: value }));
  };

  /** One serial box per (line, identity) pair — a kit line has several. */
  const serialKey = (item: PurchaseOrderItem, target: SerialTarget) =>
    `${item.id}:${target.item}`;

  /**
   * Units of `target` that receiving `quantity` on this line credits.
   *
   * `target.quantity` is what the FULL ordered quantity implies, so a partial
   * receipt scales it down by the same bill-of-materials ratio the server
   * applies. Guarded against a zero ordered quantity, which would otherwise
   * ask for an impossible number of serials.
   */
  const expectedSerialCount = (
    item: PurchaseOrderItem,
    target: SerialTarget,
    quantity: number,
  ): number => {
    const ordered = item.quantity_ordered;
    if (!ordered || ordered <= 0) return target.quantity;
    return Math.max(0, Math.round((target.quantity / ordered) * quantity));
  };

  const receiveQtyFor = (item: PurchaseOrderItem): number => {
    const raw = receiveQuantities[item.id];
    if (raw === undefined || raw.trim() === '') return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isNaN(n) || n <= 0 ? 0 : n;
  };

  const handleSubmitReceiveItems = async () => {
    if (!order || saving) return;

    const lines: ReceiveLine[] = [];
    // What the operator will be told BEFORE anything is sent, so a receipt
    // that records fewer serials than units — or more than was ordered — is
    // never a surprise discovered afterwards.
    const advisories: string[] = [];

    for (const item of getReceivableItems(order)) {
      const raw = receiveQuantities[item.id];
      if (raw === undefined || raw.trim() === '') continue;
      const quantity = Number.parseInt(raw, 10);
      if (Number.isNaN(quantity) || quantity <= 0) continue;

      const { itemName } = getItemNameAndSku(item);
      const line: ReceiveLine = {
        purchase_order_item: Number(item.id),
        quantity_received: quantity,
      };

      // An over-receipt is RECORDED, not refused and never rounded down — the
      // whole point is an honest record of what turned up. It is called out so
      // the operator can catch a typo before it becomes a vendor query.
      if (quantity > item.quantity_pending) {
        advisories.push(
          `${itemName}: receiving ${quantity} against ${item.quantity_pending} outstanding — ` +
            'recorded as an over-receipt.',
        );
      }

      // Serials go against the identity the API says this line credits, which
      // on a kit line is the COMPONENT, never the kit.
      const targets = item.serial_targets ?? [];
      if (targets.length > 0) {
        const serials: ReceiveLine['serials'] = [];
        for (const target of targets) {
          const key = serialKey(item, target);
          const entered = parseSerialNumbers(serialInputs[key] ?? '');
          const expected = expectedSerialCount(item, target, quantity);
          if (entered.length > expected) {
            showError(
              `Enter at most ${expected} serial number${expected === 1 ? '' : 's'} for ` +
                `${target.item_name} (got ${entered.length}). Nothing has been received.`,
            );
            return;
          }
          if (entered.length > 0 && entered.length < expected) {
            advisories.push(
              `${target.item_name}: ${entered.length} of ${expected} serials captured — ` +
                'the rest can be added from the item page.',
            );
          }
          const lot = (serialLots[key] ?? '').trim();
          const expiry = (serialExpiries[key] ?? '').trim();
          for (const serial_number of entered) {
            serials.push({
              serial_number,
              item: target.item,
              ...(lot ? { lot } : {}),
              ...(expiry ? { expiration_date: expiry } : {}),
            });
          }
        }
        if (serials.length > 0) line.serials = serials;
      }

      if (closeShortLines[item.id]) {
        line.close_short = true;
        const reason = (closeShortReasons[item.id] ?? '').trim();
        if (reason) line.close_short_reason = reason;
      }

      lines.push(line);
    }

    if (lines.length === 0) {
      showError('Enter a quantity for at least one item to receive');
      return;
    }

    // Never silently: anything unusual about this receipt is said out loud and
    // confirmed before it is recorded. The receipt is driven from the confirm
    // callback, so declining leaves the panel and the typed quantities exactly
    // as they were.
    if (advisories.length > 0) {
      confirmAction(
        'Record this receipt?',
        `${advisories.join(' ')} Record the receipt as entered?`,
        () => void submitReceipt(lines),
        { labels: { confirm: 'Record receipt', cancel: 'Go back' } },
      );
      return;
    }

    await submitReceipt(lines);
  };

  /** Send the resolved lines and patch the page from the response. */
  const submitReceipt = async (lines: ReceiveLine[]) => {
    try {
      setSaving(true);
      // ONE call. Serials ride inside the receipt's own transaction, so a
      // serial that cannot be saved rolls the whole receipt back instead of
      // leaving stock credited and the operator's serials lost — which is what
      // the previous loop of N follow-up calls could do.
      const response = await purchaseOrderAPI.receiveItems(orderId!, {
        items: lines,
        delivery_date: receiveDeliveryDate || undefined,
        tracking_number: receiveTracking || undefined,
        carrier: receiveCarrier || undefined,
        receipt_notes: receiveNotes || undefined,
      });
      if (response.data && typeof response.data === 'object' && response.data.id) {
        setOrder(response.data as PurchaseOrder);
      }

      const serialCount = lines.reduce((sum, line) => sum + (line.serials?.length ?? 0), 0);
      handleCancelReceiveItems();
      showSuccess(
        serialCount > 0
          ? `Items received; recorded ${serialCount} serialized unit${serialCount === 1 ? '' : 's'}.`
          : 'Items received',
      );
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to receive items'));
      console.error('Error receiving items:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReceived = async () => {
    if (!order || saving) return;
    const outstanding = getReceivableItems(order);
    const reason = await promptInput(
      'Close out this order?',
      `${outstanding.length} line${outstanding.length === 1 ? '' : 's'} will be recorded as ` +
        'short — what did not arrive is written off, not marked received. The order reads ' +
        'as received only if something actually arrived against it. Reason (optional):',
    );
    // `null` is Cancel; `''` is "confirmed, no reason typed". Different
    // answers, and only one of them is a decision to close the order.
    if (reason === null) return;

    try {
      setSaving(true);
      const response = await purchaseOrderAPI.markReceived(orderId!, { reason });
      const settled =
        response.data && typeof response.data === 'object' && response.data.id
          ? (response.data as PurchaseOrder)
          : null;
      if (settled) setOrder(settled);

      // Report what the server actually did, not what the request asked for.
      // Writing every line off does not make an order received when nothing
      // ever arrived — that status is a claim goods turned up — so the order
      // stays where it was, and saying "closed out" over a hero still reading
      // "Sent" would be the screen contradicting itself.
      if (settled && settled.status !== 'received') {
        showSuccess(
          'Outstanding lines written off. Nothing was received against this order, so it is ' +
            `still ${settled.status_label.toLowerCase()} — ` +
            (isStaff
              ? 'use Void PO to finish with it.'
              : 'ask a staff member to void or cancel the order to finish with it.'),
        );
      } else {
        showSuccess('Purchase order closed out');
      }
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to mark purchase order received'));
    } finally {
      setSaving(false);
    }
  };

  const handleReopenShort = async (item: PurchaseOrderItem) => {
    if (!order || saving) return;
    const { itemName } = getItemNameAndSku(item);
    // The server already sends this: `quantity_pending` is the same subtraction
    // floored at zero, and re-doing it here is a second derivation of what
    // receiving is still owed that can only ever drift from the first.
    const outstanding = item.quantity_pending;
    const reason = await promptInput(
      'Reopen this line?',
      `${itemName} was closed short with ${outstanding} unit${outstanding === 1 ? '' : 's'} ` +
        'written off. Reopening puts them back on the order; the close-short stays on the ' +
        'record beside this correction. Reason (optional):',
    );
    // `null` is Cancel; `''` is "confirmed, no reason typed". Different
    // answers, and only one of them is a decision to reopen the line.
    if (reason === null) return;

    try {
      setSaving(true);
      const response = await purchaseOrderAPI.reopenShort(orderId!, {
        items: [{ purchase_order_item: Number(item.id), reason }],
      });
      if (response.data && typeof response.data === 'object' && response.data.id) {
        setOrder(response.data as PurchaseOrder);
      }
      showSuccess('Line reopened — it is outstanding again');
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to reopen line'));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditMetadata = () => {
    if (!order) return;
    setMetadataSupplierOrderNumber(order.supplier_order_number || '');
    setMetadataSalesOrderNumber(order.sales_order_number || '');
    setMetadataExpectedDelivery(order.expected_delivery_date || '');
    setMetadataOrderDate(utcYmd(order.order_date));
    setMetadataPriority(order.priority || 'normal');
    setMetadataPaymentTerms(order.payment_terms || '');
    setMetadataFreightTerms(order.freight_terms || '');
    setMetadataWorkOrder(order.work_order || '');
    setMetadataCommittee(order.owning_group ? String(order.owning_group) : '');
    setEditingMetadata(true);
  };

  const handleCancelEditMetadata = () => {
    setEditingMetadata(false);
    setMetadataSupplierOrderNumber('');
    setMetadataSalesOrderNumber('');
    setMetadataExpectedDelivery('');
    setMetadataOrderDate('');
    setMetadataPriority('normal');
    setMetadataPaymentTerms('');
    setMetadataFreightTerms('');
    setMetadataWorkOrder('');
    setMetadataCommittee('');
  };

  const handleSaveMetadata = async () => {
    try {
      setSaving(true);
      await purchaseOrderAPI.updateOrder(orderId!, {
        supplier_order_number: metadataSupplierOrderNumber,
        sales_order_number: metadataSalesOrderNumber,
        expected_delivery_date: metadataExpectedDelivery || null,
        // Header terms (op-bwo9). The order date is edited as a day but the
        // field is a datetime, so send midday UTC — the day the operator
        // picked is the day the payment schedule is derived from. A cleared
        // date would leave the order without one, so it is only sent when set.
        ...(metadataOrderDate && { order_date: ymdToUtcDateTime(metadataOrderDate) }),
        priority: metadataPriority,
        // '' is a real value for both terms fields: "not agreed yet".
        payment_terms: metadataPaymentTerms,
        freight_terms: metadataFreightTerms,
        // Empty picker means "no association" — send null to clear it.
        work_order: metadataWorkOrder || null,
        owning_group: metadataCommittee ? Number(metadataCommittee) : null,
      });
      await loadOrder();
      setEditingMetadata(false);
      showSuccess('Purchase order details updated');
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to update purchase order details'));
      console.error('Error updating PO metadata:', err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Put ONE add-line control into a named state — the single place that decides
   * what pending, refused and added each look like.
   *
   * INVARIANT: a state applies to the named control and never touches the
   * sibling's error, notice, input fields or candidates. The two controls are
   * independent by design, and wiping the sibling's messages is worse than
   * leaving them — an ambiguity prompt cleared while its own choose-one buttons
   * stay on screen leaves the operator with two unexplained "Add <item>"
   * buttons under a message about a different line, which invites putting the
   * wrong item on the order. Equally, a scan must not wipe a half-typed custom
   * line.
   *
   * - `pending` — the submit was accepted and a request is going out. Clears
   *   this control's error and notice, leaves its fields alone so the
   *   operator's typing survives the round trip, and leaves candidates alone.
   * - `refused` — any refusal, client-side or server-side, identical either
   *   way: this control's error set, its stale confirmation cleared, its fields
   *   left alone so the operator can correct them. Candidates belong to the
   *   identifier control only, so an ambiguous refusal shows its choose-one set
   *   and every other refusal clears it; a custom-line refusal leaves the scan
   *   field's pending choices exactly where they were, because a freeform line
   *   has no identifier to be ambiguous about.
   * - `added` — a successful add: this control's confirmation set, its error
   *   cleared, and the fields it has just consumed emptied for the next one.
   */
  const setAddLineFormState = (control: AddLineControl, state: AddLineFormState) => {
    const isCustom = control === 'custom';
    const setError = isCustom ? setCustomLineError : setAddLineError;
    const setNotice = isCustom ? setCustomLineNotice : setAddLineNotice;

    switch (state.kind) {
      case 'pending':
        setError(null);
        setNotice(null);
        break;
      case 'refused':
        setNotice(null);
        setError(state.message);
        if (!isCustom) {
          setAddLineCandidates(state.candidates ?? []);
        }
        break;
      case 'added':
        setError(null);
        setNotice(state.notice);
        if (isCustom) {
          setCustomLineDescription('');
          setCustomLineUnitCost('');
          setCustomLineQuantity('1');
        } else {
          setAddLineIdentifier('');
          setAddLineCandidates([]);
        }
        break;
    }
  };

  /**
   * Add one line from a typed or scanned identifier (oms-po-add-item).
   *
   * The server owns every rule — draft-only, does-this-supplier-supply-it, and
   * what to do when the item is already on the order — so this handler only
   * routes the answers: a 409 carries the candidate set to choose from, any
   * other failure is a message to show. The success path patches the page from
   * the response's full purchase order rather than re-running the initial
   * loader (docs/REACTIVE_MUTATIONS.md), so the operator's scroll position and
   * the next scan's focus survive the add.
   */
  const submitAddLine = async (
    payload: AddPurchaseOrderLinePayload,
    // Which control the add came from. Decides where a refusal is shown and
    // whether the caret goes back to the scan field afterwards.
    source: AddLineControl = 'identifier',
  ) => {
    const fromCustom = source === 'custom';
    try {
      // Source-gated: the scanner-focus loop belongs to the identifier field.
      addLineCameFromScanField.current = !fromCustom;
      // Shared by decision: one in-flight flag for the whole add. Both forms
      // read it to go read-only and to refuse re-entry, and only one add can
      // be in flight at a time, so splitting it per control would let the
      // second submit sail past the first.
      setAddingLine(true);
      setAddLineFormState(source, { kind: 'pending' });
      const response = await purchaseOrderAPI.addLineItem(orderId!, payload);
      const { created, line_item: lineItem, match, purchase_order: refreshed } = response.data;

      // Shared by decision: the purchase order is the page's single subject and
      // every control on it renders from this one copy.
      setOrder(refreshed);

      // The same three-way the items table renders through, so the
      // confirmation and the table can never name one line differently.
      const itemName = getItemNameAndSku(lineItem).itemName;
      const matchedBy = match ? ` (matched on ${match.match_label} ${match.matched_value})` : '';
      const notice = created
        ? `Added ${itemName} × ${lineItem.quantity_ordered}${matchedBy}`
        : `${itemName} was already on this order — quantity is now ${lineItem.quantity_ordered}${matchedBy}`;
      // Answer the operator where they are: a custom line is confirmed under
      // the custom-line form, not in the identifier form's status line further
      // up the page where the operator has no reason to look for it.
      setAddLineFormState(source, { kind: 'added', notice });
      // Shared by decision: a page-level toast, not a per-control slot.
      showSuccess(created ? `Added ${itemName}` : `Updated ${itemName}`);
    } catch (err: any) {
      const data = err?.response?.data;
      const message =
        typeof data?.error === 'string'
          ? data.error
          : extractErrorMessage(err, 'Failed to add line item');
      const candidates =
        data?.code === 'ambiguous' && Array.isArray(data.candidates) ? data.candidates : undefined;
      setAddLineFormState(source, { kind: 'refused', message, candidates });
    } finally {
      setAddingLine(false);
    }
  };

  /**
   * Add a freeform line — something the catalogue does not carry.
   *
   * A price is required here rather than optional, because there is no supplier
   * relationship or purchase history to derive one from; the server enforces
   * the same rule, so this only saves a round trip on an obvious mistake.
   */
  const handleAddCustomLineSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // Not a refusal — a duplicate submit dropped on the floor. It has nothing
    // to say, so it must not disturb any message.
    if (addingLine) {
      return;
    }
    // A client-side refusal goes through the same `refused` state a
    // server-side one does, so the two are indistinguishable on screen.
    const refuse = (message: string) =>
      setAddLineFormState('custom', { kind: 'refused', message });
    const description = customLineDescription.trim();
    if (!description) {
      refuse('Describe what is being bought on this line.');
      return;
    }
    if (!customLineUnitCost.trim()) {
      refuse('A custom line needs a unit cost — there is no catalogue price to use.');
      return;
    }
    const quantity = Number(customLineQuantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      refuse('Quantity must be a whole number of one or more.');
      return;
    }
    void submitAddLine(
      { description, unit_cost: customLineUnitCost.trim(), quantity },
      'custom',
    );
  };

  const handleAddLineSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // The field stays focused (and therefore submittable) while a request is in
    // flight, so a second Enter must not start a second add. Deliberately not a
    // state transition: a duplicate submit dropped on the floor has nothing to
    // say, so it must not disturb any message.
    if (addingLine) {
      return;
    }
    const identifier = addLineIdentifier.trim();
    if (!identifier) {
      // Same rule as the custom-line form: a client-side refusal goes through
      // the same `refused` state a server-side one does, so the confirmation
      // the last scan earned and any pending choose-one set go with it rather
      // than standing on under a message about something else.
      setAddLineFormState('identifier', {
        kind: 'refused',
        message: 'Type or scan an item name, SKU, barcode, or supplier SKU.',
      });
      return;
    }
    void submitAddLine({ identifier });
  };

  const handleUploadAttachment = async () => {
    if (!attachmentFile) {
      showError('Please choose a file to upload');
      return;
    }

    try {
      setUploadingAttachment(true);
      await purchaseOrderAPI.uploadAttachment(orderId!, attachmentFile, attachmentDescription);
      await loadOrder();
      setAttachmentFile(null);
      setAttachmentDescription('');
      showSuccess('Attachment uploaded');
    } catch (err: any) {
      showError(extractErrorMessage(err, 'Failed to upload attachment'));
      console.error('Error uploading attachment:', err);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = (attachment: PurchaseOrderAttachment) => {
    confirmAction(
      'Delete attachment?',
      `Remove "${attachment.file_name || attachment.description || 'this attachment'}" from the purchase order? This cannot be undone.`,
      async () => {
        try {
          setSaving(true);
          await purchaseOrderAPI.deleteAttachment(orderId!, attachment.id);
          await loadOrder();
          showSuccess('Attachment deleted');
        } catch (err: any) {
          showError(extractErrorMessage(err, 'Failed to delete attachment'));
          console.error('Error deleting attachment:', err);
        } finally {
          setSaving(false);
        }
      },
      { labels: { confirm: 'Delete', cancel: 'Cancel' }, color: 'red' },
    );
  };

  const formatDate = (dateString: string | null) =>
    formatDateOnly(dateString, { year: 'numeric', month: 'short', day: 'numeric' });

  const formatCurrency = (value: string | null) => {
    if (!value) return '—';
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(num);
  };

  if (loading) {
    return (
      <WorkspacePage
        testId="purchase-order-page"
        hero={{ eyebrow: 'Purchasing · Order', title: 'Purchase order', description: 'Loading…' }}
      >
        <Paper withBorder p="md">
          <Text c="dimmed">Loading purchase order…</Text>
        </Paper>
      </WorkspacePage>
    );
  }

  if (error || !order) {
    return (
      <WorkspacePage
        testId="purchase-order-page"
        hero={{
          eyebrow: 'Purchasing · Order',
          title: 'Purchase order',
          description: error || 'Not found.',
        }}
      >
        <Paper withBorder p="md" radius="md" bg="red.0" c="red.9">
          <Text>{error || 'Purchase order not found'}</Text>
        </Paper>
      </WorkspacePage>
    );
  }

  const receivableItems = getReceivableItems(order);

  // Detail-page running total (op-r1sg). Summed from the rendered lines rather
  // than read off order.estimated_total so it stays live when a line is cost-
  // edited or voided in place; the two should agree, which makes the footer a
  // free sanity check on the stored create-time snapshot.
  //
  // Voided lines are excluded (matching the backend's effective_estimated_total)
  // and each line contributes exactly what its "Line Cost" cell displays —
  // actual where known, estimated otherwise. Where those diverge, the footer
  // also spells out the pure estimated sum, which is what estimated_total holds.
  const activeItems = order.items.filter((item) => !item.is_voided);
  const sumLines = (pick: (item: PurchaseOrderItem) => string | null) =>
    activeItems.reduce((total, item) => total + (parseFloat(pick(item) || '0') || 0), 0);
  const lineCostTotal = sumLines((item) => item.actual_cost || item.estimated_cost);
  const estimatedLineTotal = sumLines((item) => item.estimated_cost);
  const totalsDiffer = lineCostTotal.toFixed(2) !== estimatedLineTotal.toFixed(2);

  // Status-gated hero affordances: Send (draft→sent) and Confirm (sent→confirmed)
  // surface the existing PO lifecycle transitions alongside receive/mark-delivered.
  const heroActions: React.ReactNode[] = [];
  if (canSendToSupplier(order)) {
    heroActions.push(
      <Button key="send-to-supplier" onClick={handleSendToSupplier} disabled={transitioning}>
        Send to Supplier
      </Button>,
    );
  }
  if (canConfirmOrder(order)) {
    heroActions.push(
      <Button key="confirm-order" onClick={handleConfirmOrder} disabled={transitioning}>
        Confirm
      </Button>,
    );
  }
  // Order export: turn this PO's lines into a vendor-ready order artifact whose
  // shape follows the supplier's ordering adapter (op-svpq) — an Amazon add-to-
  // cart link, an HD Supply Part#,Qty CSV, or the generic part#,qty pad. Login-
  // gated (like the API) and only offered when there's a non-voided line to
  // order.
  if (isAuthenticated && order.items.some((item) => !item.is_voided)) {
    const adapter = order.supplier_ordering_adapter;
    if (adapter === 'amazon') {
      heroActions.push(
        <Button
          key="open-amazon-cart"
          variant="default"
          onClick={handleOpenAmazonCart}
          disabled={exportingOrderPad}
        >
          Open Amazon cart
        </Button>,
      );
    } else if (adapter === 'hdsupply') {
      heroActions.push(
        <Button
          key="download-order-pad"
          variant="default"
          onClick={handleDownloadOrderPad}
          disabled={exportingOrderPad}
        >
          Download for HD Supply
        </Button>,
        <Button
          key="copy-order-pad"
          variant="default"
          onClick={handleCopyOrderPad}
          disabled={exportingOrderPad}
        >
          Copy order pad
        </Button>,
      );
    } else {
      heroActions.push(
        <Button
          key="download-order-pad"
          variant="default"
          onClick={handleDownloadOrderPad}
          disabled={exportingOrderPad}
        >
          Download order pad (CSV)
        </Button>,
        <Button
          key="copy-order-pad"
          variant="default"
          onClick={handleCopyOrderPad}
          disabled={exportingOrderPad}
        >
          Copy order pad
        </Button>,
      );
    }
  }
  if (canReceiveItems(order) && !markingDelivered && !receivingItems) {
    heroActions.push(
      <Button key="receive-items" onClick={handleOpenReceiveItems}>
        Receive items
      </Button>,
      <Button key="mark-delivered" variant="default" onClick={handleOpenMarkDelivered}>
        Mark as delivered
      </Button>,
    );
    // Step 6 of the receiving flow, and deliberately NOT the same as "Mark as
    // delivered". That one asserts everything outstanding arrived and stocks
    // it; this one writes the shortfall off. Only offered when something is
    // actually outstanding, so it can never be mistaken for a no-op.
    if (order.outstanding_line_count > 0) {
      heroActions.push(
        <Button
          key="mark-received"
          variant="default"
          onClick={handleMarkReceived}
          disabled={saving}
          data-testid="mark-received-button"
        >
          Close out order
        </Button>,
      );
    }
  }

  return (
    <WorkspacePage
      testId="purchase-order-page"
      hero={{
        eyebrow: `Purchasing · ${order.supplier_details}`,
        title: `PO ${order.po_number}`,
        description: order.status_label,
        action: heroActions.length > 0 ? <Group gap="sm">{heroActions}</Group> : undefined,
      }}
    >
      <div className="purchase-order-page">
        {/* The mismatch the captain chases a vendor with, stated at the top of
            the order and NOT only inside the receive form — it has to stay
            visible long after the receipt, which is the whole point of
            recording it. */}
        {order.has_receipt_variance && (
          <Paper
            withBorder
            p="sm"
            radius="md"
            bg="orange.0"
            c="orange.9"
            mb="md"
            data-testid="receipt-variance-warning"
          >
            <Text size="sm">
              {order.variance_line_count}{' '}
              {order.variance_line_count === 1 ? 'line' : 'lines'} did not match what was
              ordered — see the flags in the Received column.
              {order.outstanding_line_count > 0 &&
                ` ${order.outstanding_line_count} ${
                  order.outstanding_line_count === 1 ? 'line is' : 'lines are'
                } still outstanding.`}
            </Text>
          </Paper>
        )}
        {order.serials_outstanding > 0 && (
          <Paper
            withBorder
            p="sm"
            radius="md"
            bg="yellow.0"
            c="yellow.9"
            mb="md"
            data-testid="serials-outstanding-warning"
          >
            <Text size="sm">
              {order.serials_outstanding} received{' '}
              {order.serials_outstanding === 1 ? 'unit is' : 'units are'} in stock with no serial
              number recorded. Serial numbers can be added from the item page.
            </Text>
          </Paper>
        )}
        {orderPad && orderPad.missing_sku.length > 0 && (
          <Paper
            withBorder
            p="sm"
            radius="md"
            bg="yellow.0"
            c="yellow.9"
            mb="md"
            data-testid="order-pad-missing-sku-warning"
          >
            <Text size="sm">
              {orderPad.missing_sku.length}{' '}
              {orderPad.missing_sku.length === 1 ? 'line has' : 'lines have'} no supplier
              part number — fix the item supplier before ordering.
            </Text>
          </Paper>
        )}
        {orderPad && orderPad.invalid_sku.length > 0 && (
          <Paper
            withBorder
            p="sm"
            radius="md"
            bg="red.0"
            c="red.9"
            mb="md"
            data-testid="order-pad-invalid-sku-warning"
          >
            <Text size="sm">
              {orderPad.invalid_sku.length}{' '}
              {orderPad.invalid_sku.length === 1 ? 'item has' : 'items have'}{' '}
              {orderPad.adapter === 'amazon' ? 'an invalid ASIN' : 'an invalid part number'} —
              fix the item supplier part number before ordering.
            </Text>
          </Paper>
        )}
        {orderPad?.adapter === 'amazon' && (orderPad.cart_urls?.length ?? 0) > 1 && (
          <Paper withBorder p="sm" radius="md" mb="md" data-testid="amazon-cart-chunks">
            <Text size="sm" mb="xs">
              This order is split across {orderPad.cart_urls!.length} Amazon carts (Amazon
              limits cart-URL length). Open each:
            </Text>
            <Group gap="sm">
              {orderPad.cart_urls!.map((url, index) => (
                <Button
                  key={`amazon-cart-${index}`}
                  size="xs"
                  variant="light"
                  onClick={() => openAmazonCart(url)}
                >
                  Open cart {index + 1}/{orderPad.cart_urls!.length}
                </Button>
              ))}
            </Group>
          </Paper>
        )}
        <div className="po-status">
          {canVoidOrder(order) && (
            <button
              type="button"
              className="btn-danger void-po-button"
              onClick={handleVoidOrder}
              disabled={saving}
            >
              Void PO
            </button>
          )}
        </div>

      {order.status === 'voided' && (
        <section className="po-voided-banner" aria-label="Voided purchase order">
          <span className="status-badge status-voided">VOIDED</span>
          {order.void_reason && (
            <p className="void-reason">
              <strong>Reason:</strong> {order.void_reason}
            </p>
          )}
          {order.voided_by_username && (
            <p className="void-meta">
              Voided by {order.voided_by_username}
              {order.voided_at && ` on ${formatDate(order.voided_at)}`}
            </p>
          )}
        </section>
      )}

      {markingDelivered && (
        <section className="mark-delivered-panel" aria-label="Mark purchase order as delivered">
          <h2>Mark as Delivered</h2>
          <div className="mark-delivered-fields">
            <label htmlFor="mark-delivered-date">
              Delivery Date
              <input
                id="mark-delivered-date"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                required
              />
            </label>
            <label htmlFor="mark-delivered-tracking">
              Tracking Number (optional)
              <input
                id="mark-delivered-tracking"
                type="text"
                value={deliveryTracking}
                onChange={(e) => setDeliveryTracking(e.target.value)}
                placeholder="e.g. 1Z999AA10123456784"
              />
            </label>
            <label htmlFor="mark-delivered-carrier">
              Carrier (optional)
              <input
                id="mark-delivered-carrier"
                type="text"
                value={deliveryCarrier}
                onChange={(e) => setDeliveryCarrier(e.target.value)}
                placeholder="e.g. UPS"
              />
            </label>
          </div>
          <div className="mark-delivered-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmitMarkDelivered}
              disabled={saving || !deliveryDate}
            >
              {saving ? 'Saving…' : 'Confirm Delivery'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancelMarkDelivered}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {/* Say WHY receiving is off rather than silently dropping the buttons.
          Hidden while the receive/mark-delivered panels are open, since the
          affordance is plainly present in that state. */}
      {isAuthenticated && !receivingItems && !markingDelivered && receiveUnavailableReason(order) && (
        <section
          className="receive-unavailable-notice"
          aria-label="Why receiving is unavailable"
          data-testid="receive-unavailable-notice"
        >
          <p>{receiveUnavailableReason(order)}</p>
        </section>
      )}

      {receivingItems && (
        <section className="receive-items-panel" aria-label="Receive purchase order items">
          <h2>Receive Items</h2>
          <div className="receive-items-fields">
            <label htmlFor="receive-delivery-date">
              Delivery Date (optional)
              <input
                id="receive-delivery-date"
                type="date"
                value={receiveDeliveryDate}
                onChange={(e) => setReceiveDeliveryDate(e.target.value)}
              />
            </label>
            <label htmlFor="receive-tracking">
              Tracking Barcode (optional)
              <input
                id="receive-tracking"
                type="text"
                value={receiveTracking}
                onChange={(e) => setReceiveTracking(e.target.value)}
                placeholder="Scan the carrier label"
                autoComplete="off"
              />
            </label>
            <label htmlFor="receive-carrier">
              Carrier (optional)
              <input
                id="receive-carrier"
                type="text"
                value={receiveCarrier}
                onChange={(e) => setReceiveCarrier(e.target.value)}
                placeholder="e.g. UPS"
              />
            </label>
            <label htmlFor="receive-notes">
              Receipt Notes (optional)
              <input
                id="receive-notes"
                type="text"
                value={receiveNotes}
                onChange={(e) => setReceiveNotes(e.target.value)}
                placeholder="e.g. Partial shipment, backorder to follow"
              />
            </label>
          </div>
          {receivableItems.length === 0 ? (
            <p className="no-data">
              Receiving has finished with every line on this order — each was received,
              written off, or struck off.
            </p>
          ) : (
            <table className="items-table receive-items-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>SKU</th>
                  <th>Pending</th>
                  <th>Receive Qty</th>
                </tr>
              </thead>
              <tbody>
                {receivableItems.map((item) => {
                  const { itemName, itemSku } = getItemNameAndSku(item);
                  const targets = item.serial_targets ?? [];
                  const qty = receiveQtyFor(item);
                  // A short receipt the operator has NOT declared final is
                  // ordinary — the rest may still be coming — so this is an
                  // offer, never a block.
                  const short = qty > 0 && qty < item.quantity_pending;
                  const over = qty > item.quantity_pending;
                  return (
                    <React.Fragment key={item.id}>
                      <tr>
                        <td>
                          {itemName}
                          {targets.length > 0 && (
                            <span className="receive-serial-tag"> · serialized</span>
                          )}
                        </td>
                        <td>{itemSku}</td>
                        <td>{item.quantity_pending}</td>
                        <td>
                          {/* No `max`: more than was ordered is a real thing
                              that happens, and the record has to be able to
                              say so. The operator is told, then it is saved
                              exactly as entered. */}
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className="receive-items-qty-input"
                            value={receiveQuantities[item.id] ?? ''}
                            onChange={(e) => handleReceiveQuantityChange(item.id, e.target.value)}
                            disabled={saving}
                            aria-label={`Receive quantity for ${itemName}`}
                          />
                          {over && (
                            <span
                              className="receive-variance-note receive-variance-over"
                              data-testid={`receive-over-note-${item.id}`}
                            >
                              +{qty - item.quantity_pending} over — recorded and flagged
                            </span>
                          )}
                        </td>
                      </tr>
                      {short && (
                        <tr
                          className="receive-serial-row"
                          data-testid={`receive-short-row-${item.id}`}
                        >
                          <td colSpan={4}>
                            <label className="receive-close-short">
                              <input
                                type="checkbox"
                                checked={Boolean(closeShortLines[item.id])}
                                onChange={(e) =>
                                  setCloseShortLines((prev) => ({
                                    ...prev,
                                    [item.id]: e.target.checked,
                                  }))
                                }
                                disabled={saving}
                              />{' '}
                              The remaining {item.quantity_pending - qty}{' '}
                              {item.quantity_pending - qty === 1 ? 'unit is' : 'units are'} not
                              coming — close this line short
                            </label>
                            {closeShortLines[item.id] && (
                              <input
                                type="text"
                                className="receive-close-short-reason"
                                value={closeShortReasons[item.id] ?? ''}
                                onChange={(e) =>
                                  setCloseShortReasons((prev) => ({
                                    ...prev,
                                    [item.id]: e.target.value,
                                  }))
                                }
                                disabled={saving}
                                placeholder="Reason (e.g. backorder cancelled)"
                                aria-label={`Reason for closing ${itemName} short`}
                              />
                            )}
                          </td>
                        </tr>
                      )}
                      {/* Live consequence row (op-8n0). Driven ENTIRELY by
                          local state and the line's own snapshot, so it updates
                          as the quantity is typed and the operator sees what
                          receiving will do BEFORE committing. Mirrors the
                          serialized disclosure sub-row directly below. */}
                      {item.is_kit_line && qty > 0 && (item.kit_components?.length ?? 0) > 0 && (
                        <tr
                          className="receive-serial-row"
                          data-testid={`receive-kit-consequence-${item.id}`}
                        >
                          <td colSpan={4}>
                            Receiving {qty} {qty === 1 ? 'kit' : 'kits'} adds{' '}
                            {(item.kit_components ?? []).reduce(
                              (sum, component) => sum + component.quantity_per_kit * qty,
                              0
                            )}{' '}
                            units across {(item.kit_components ?? []).length} items
                          </td>
                        </tr>
                      )}
                      {/* One capture block per serialized IDENTITY this line
                          credits — on a kit line that is each component, and
                          never the kit. Driven by the server's
                          `serial_targets` rather than the line's own
                          `is_serialized`, because on a kit line that flag
                          describes a SKU that never enters stock. */}
                      {qty > 0 &&
                        targets.map((target) => {
                          const key = serialKey(item, target);
                          const expected = expectedSerialCount(item, target, qty);
                          const entered = parseSerialNumbers(serialInputs[key] ?? '').length;
                          return (
                            <tr
                              className="receive-serial-row"
                              key={key}
                              data-testid={`receive-serials-${key}`}
                            >
                              <td colSpan={4}>
                                <label htmlFor={`serials-${key}`}>
                                  Serial numbers for {target.item_name} — one per line ({entered}/
                                  {expected})
                                  {item.is_kit_line && (
                                    <span className="receive-serial-tag">
                                      {' '}
                                      · component of {itemName}
                                    </span>
                                  )}
                                </label>
                                <textarea
                                  id={`serials-${key}`}
                                  className="receive-serials-input"
                                  rows={Math.min(Math.max(expected, 2), 8)}
                                  value={serialInputs[key] ?? ''}
                                  onChange={(e) => handleSerialInputChange(key, e.target.value)}
                                  disabled={saving}
                                  placeholder={'SN-0001\nSN-0002'}
                                  aria-label={`Serial numbers for ${target.item_name}`}
                                />
                                {entered > 0 && entered < expected && (
                                  <p className="receive-variance-note">
                                    {entered} of {expected} captured — the rest can be added from
                                    the item page.
                                  </p>
                                )}
                                {/* Lot and expiry apply to every serial in
                                    this box: one box is one batch off one
                                    pallet, which is what a lot number means. */}
                                <div className="receive-serial-meta">
                                  <label htmlFor={`lot-${key}`}>
                                    Lot (optional)
                                    <input
                                      id={`lot-${key}`}
                                      type="text"
                                      value={serialLots[key] ?? ''}
                                      onChange={(e) =>
                                        setSerialLots((prev) => ({ ...prev, [key]: e.target.value }))
                                      }
                                      disabled={saving}
                                      placeholder="Batch / lot number"
                                    />
                                  </label>
                                  <label htmlFor={`expiry-${key}`}>
                                    Expiry (optional)
                                    <input
                                      id={`expiry-${key}`}
                                      type="date"
                                      value={serialExpiries[key] ?? ''}
                                      onChange={(e) =>
                                        setSerialExpiries((prev) => ({
                                          ...prev,
                                          [key]: e.target.value,
                                        }))
                                      }
                                      disabled={saving}
                                    />
                                  </label>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="receive-items-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmitReceiveItems}
              disabled={saving || receivableItems.length === 0}
            >
              {saving ? 'Saving…' : 'Confirm Receipt'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancelReceiveItems}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      <div className="po-info">
        <div className="info-item">
          <span className="info-label">Date Ordered:</span>
          {/* The business day, not the viewer's local rendering of the stored
              instant — it is the day `payment_schedule` is derived from. */}
          <span className="info-value">{formatDate(utcYmd(order.order_date))}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Date Promised:</span>
          <span className="info-value">{formatDate(order.expected_delivery_date)}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Priority:</span>
          <span className="info-value">{priorityLabel(order.priority)}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Payment Terms:</span>
          <span className="info-value">{paymentTermsLabel(order.payment_terms)}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Freight:</span>
          <span className="info-value">{freightTermsLabel(order.freight_terms)}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Supplier Order #:</span>
          <span className="info-value">{order.supplier_order_number || '—'}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Sales Order #:</span>
          <span className="info-value">{order.sales_order_number || '—'}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Agreement:</span>
          <span className="info-value">{order.supplier_agreement_details?.name || '—'}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Work Order:</span>
          <span className="info-value">{workOrderDetailsLabel(order.work_order_details)}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Committee:</span>
          <span className="info-value">{order.owning_group_details?.name || '—'}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Estimated Total:</span>
          <span className="info-value">{formatCurrency(order.estimated_total)}</span>
        </div>
        {/* Derived from the terms above (op-bwo9) — never stored, so voiding a
            line moves it. Read-only: change the terms to change the payment. */}
        <div className="info-item">
          <span className="info-label">Payment schedule:</span>
          <span className="info-value">{paymentScheduleSummary(order.payment_schedule)}</span>
        </div>
      </div>

      {isAuthenticated && (
        <section className="po-metadata" aria-label="Purchase order details">
          <div className="po-metadata-header">
            <h2>Order Details</h2>
            {!editingMetadata && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleStartEditMetadata}
              >
                Edit Details
              </button>
            )}
          </div>
          {editingMetadata ? (
            <div className="po-metadata-edit">
              <label htmlFor="metadata-supplier-order">
                Supplier Order Number
                <input
                  id="metadata-supplier-order"
                  type="text"
                  value={metadataSupplierOrderNumber}
                  onChange={(e) => setMetadataSupplierOrderNumber(e.target.value)}
                  placeholder="Order number assigned by supplier"
                  maxLength={128}
                />
              </label>
              <label htmlFor="metadata-sales-order">
                Sales Order Number
                <input
                  id="metadata-sales-order"
                  type="text"
                  value={metadataSalesOrderNumber}
                  onChange={(e) => setMetadataSalesOrderNumber(e.target.value)}
                  placeholder="Sales order reference"
                  maxLength={128}
                />
              </label>
              {/* Header terms (op-bwo9). `order_date` is editable because an
                  order is often entered after it was placed. */}
              <label htmlFor="metadata-order-date">
                Date Ordered
                <input
                  id="metadata-order-date"
                  type="date"
                  value={metadataOrderDate}
                  onChange={(e) => setMetadataOrderDate(e.target.value)}
                />
              </label>
              <label htmlFor="metadata-expected-delivery">
                Date Promised (expected delivery)
                <input
                  id="metadata-expected-delivery"
                  type="date"
                  value={metadataExpectedDelivery}
                  onChange={(e) => setMetadataExpectedDelivery(e.target.value)}
                />
              </label>
              <label htmlFor="metadata-priority">
                Priority
                <select
                  id="metadata-priority"
                  value={metadataPriority}
                  onChange={(e) => setMetadataPriority(e.target.value as PurchaseOrderPriority)}
                  disabled={saving}
                >
                  {PO_PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label htmlFor="metadata-payment-terms">
                Payment Terms
                <select
                  id="metadata-payment-terms"
                  value={metadataPaymentTerms}
                  onChange={(e) =>
                    setMetadataPaymentTerms(e.target.value as PurchaseOrderPaymentTerms | '')
                  }
                  disabled={saving}
                >
                  <option value="">Not agreed</option>
                  {PO_PAYMENT_TERMS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label htmlFor="metadata-freight-terms">
                Freight Terms
                <select
                  id="metadata-freight-terms"
                  value={metadataFreightTerms}
                  onChange={(e) =>
                    setMetadataFreightTerms(e.target.value as PurchaseOrderFreightTerms | '')
                  }
                  disabled={saving}
                >
                  <option value="">Not agreed</option>
                  {PO_FREIGHT_TERMS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              {/* Order-level associations (op-shb9). Attribution only — they
                  change no cost and bill no committee. */}
              <AssociationPickers
                idPrefix="metadata"
                workOrderOptions={withCurrentOption(
                  workOrderOptions,
                  metadataWorkOrder,
                  workOrderDetailsLabel(order.work_order_details),
                )}
                committeeOptions={withCurrentOption(
                  committeeOptions,
                  metadataCommittee,
                  order.owning_group_details?.name || '',
                )}
                workOrderValue={metadataWorkOrder}
                committeeValue={metadataCommittee}
                onWorkOrderChange={setMetadataWorkOrder}
                onCommitteeChange={setMetadataCommittee}
                disabled={saving}
              />
              <div className="po-metadata-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveMetadata}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Details'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCancelEditMetadata}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </section>
      )}

      <section className="po-attachments" aria-label="Purchase order attachments">
        <h2>Attachments</h2>
        {order.attachments.length === 0 ? (
          <p className="no-data">No attachments yet.</p>
        ) : (
          <ul className="attachments-list">
            {order.attachments.map((attachment) => (
              <li key={attachment.id} className="attachment-item">
                <div className="attachment-meta">
                  {attachment.file_url ? (
                    <a
                      href={attachment.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="attachment-link"
                    >
                      {attachment.file_name || 'Download'}
                    </a>
                  ) : (
                    <span>{attachment.file_name || 'Attachment'}</span>
                  )}
                  {attachment.description && (
                    <span className="attachment-description">— {attachment.description}</span>
                  )}
                  <span className="attachment-uploader">
                    {attachment.uploaded_by_name
                      ? `Uploaded by ${attachment.uploaded_by_name}`
                      : 'Uploaded'}{' '}
                    on {formatDate(attachment.uploaded_at)}
                  </span>
                </div>
                {isStaff && (
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => handleDeleteAttachment(attachment)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {isAuthenticated && (
          <div className="attachment-upload">
            <h3>Upload Attachment</h3>
            <label htmlFor="attachment-file">
              File
              <input
                id="attachment-file"
                type="file"
                onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                disabled={uploadingAttachment}
              />
            </label>
            <label htmlFor="attachment-description">
              Description (optional)
              <input
                id="attachment-description"
                type="text"
                value={attachmentDescription}
                onChange={(e) => setAttachmentDescription(e.target.value)}
                placeholder="e.g. Sales order from supplier"
                maxLength={500}
                disabled={uploadingAttachment}
              />
            </label>
            <button
              type="button"
              className="btn-primary"
              onClick={handleUploadAttachment}
              disabled={uploadingAttachment || !attachmentFile}
            >
              {uploadingAttachment ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        )}
      </section>

      <section className="po-items">
        <h2>Line Items</h2>

        {/* Add-a-line entry (oms-po-add-item). Offered ONLY on a draft the
            viewer can edit — once an order has gone to the supplier, what it
            contains is a matter of record, so the control is absent rather
            than present-and-disabled. The server enforces the same rule; this
            is only the affordance. */}
        {isAuthenticated && order.status === 'draft' && (
          <form className="po-add-line" onSubmit={handleAddLineSubmit}>
            <label htmlFor="po-add-line-identifier">
              Add an item
              <input
                id="po-add-line-identifier"
                type="text"
                ref={addLineInputRef}
                value={addLineIdentifier}
                onChange={(e) => {
                  setAddLineIdentifier(e.target.value);
                  setAddLineError(null);
                  setAddLineCandidates([]);
                }}
                placeholder="Scan a barcode, or type a name, SKU, or supplier SKU"
                // readOnly, not disabled: disabling the focused field blurs it,
                // and the next scan's character burst would land nowhere.
                readOnly={addingLine}
                autoComplete="off"
              />
            </label>
            <button type="submit" className="btn-primary" disabled={addingLine}>
              {addingLine ? 'Adding…' : 'Add to order'}
            </button>
            <p className="po-add-line-hint">
              A scanner works here: the scan lands in the field and its Enter submits it.
            </p>

            {addLineNotice && (
              <p className="po-add-line-notice" role="status">
                {addLineNotice}
              </p>
            )}

            {addLineError && (
              <p className="po-add-line-error" role="alert">
                {addLineError}
              </p>
            )}

            {/* Every row says WHY it came up (`match_label` / `matched_value`),
                which is the whole point on the choose-one path: a cross-vendor
                match resolves through another vendor's listing, so without it
                nothing on screen would contain what the operator scanned. */}
            {addLineCandidates.length > 0 && (
              <ul className="po-add-line-candidates">
                {addLineCandidates.map((candidate) => {
                  const onOrder = candidate.already_on_order;
                  // A voided line cannot be grown — the server refuses it with
                  // `line_voided` — so this row is a dead end, not a choice.
                  const voided = Boolean(onOrder?.is_voided);
                  return (
                    <li key={candidate.item_supplier}>
                      <span className="po-add-line-candidate-name">{candidate.item.name}</span>
                      <span className="po-add-line-candidate-meta">
                        {candidate.item.sku} · supplier SKU {candidate.supplier_sku || '—'} ·
                        matched on {candidate.match_label} {candidate.matched_value}
                        {onOrder && !voided
                          ? ` · already on this order (${onOrder.quantity_ordered})`
                          : ''}
                        {voided ? ' · voided on this order — restore or remove that line first' : ''}
                      </span>
                      {voided ? null : (
                        // `btn-primary`, the same class this form's "Add to
                        // order" submit uses. Not `btn-edit`: that resolves to
                        // `background: none` on this page while a global
                        // `color: white` wins the cascade, so these buttons
                        // rendered white-on-white — invisible in both the dev
                        // server and a production build. Clickable-if-you-guess
                        // is not a shop-floor control, and the choose-one list
                        // is worthless if the operator cannot see the choices.
                        // Reusing the page's proven submit class rather than
                        // adding a third button appearance is also the right
                        // design: this button adds an item, like that one does.
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={addingLine}
                          onClick={() => submitAddLine({ item_supplier: candidate.item_supplier })}
                        >
                          Add {candidate.item.name}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </form>
        )}

        {/* The freeform line. Same endpoint, same draft-only rule — a
            different shape of line, for the thing the catalogue does not
            carry. Behind a disclosure because the identifier field above is
            the workflow and this is the exception; opening it does not disturb
            anything typed or scanned into that field. */}
        {isAuthenticated && order.status === 'draft' && (
          <div className="po-add-custom-line">
            <button
              type="button"
              className="btn-secondary"
              aria-expanded={showCustomLine}
              onClick={() => {
                setShowCustomLine((open) => !open);
                setCustomLineError(null);
                setCustomLineNotice(null);
              }}
            >
              {showCustomLine ? 'Cancel custom line' : 'Add a custom line'}
            </button>

            {showCustomLine && (
              <form onSubmit={handleAddCustomLineSubmit}>
                <p className="po-add-line-hint">
                  For something the catalogue does not carry — a one-off charge, or a part
                  bought once. It has no catalogue price, so give it one.
                </p>
                <label htmlFor="po-custom-line-description">
                  What is being bought
                  <input
                    id="po-custom-line-description"
                    type="text"
                    value={customLineDescription}
                    onChange={(e) => {
                      setCustomLineDescription(e.target.value);
                      setCustomLineError(null);
                    }}
                    placeholder="Pallet freight surcharge"
                    maxLength={500}
                    readOnly={addingLine}
                  />
                </label>
                <label htmlFor="po-custom-line-unit-cost">
                  Unit cost
                  <input
                    id="po-custom-line-unit-cost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={customLineUnitCost}
                    onChange={(e) => {
                      setCustomLineUnitCost(e.target.value);
                      setCustomLineError(null);
                    }}
                    readOnly={addingLine}
                  />
                </label>
                <label htmlFor="po-custom-line-quantity">
                  Quantity
                  <input
                    id="po-custom-line-quantity"
                    type="number"
                    min="1"
                    step="1"
                    value={customLineQuantity}
                    onChange={(e) => {
                      setCustomLineQuantity(e.target.value);
                      setCustomLineError(null);
                    }}
                    readOnly={addingLine}
                  />
                </label>
                {customLineNotice && (
                  <p className="po-add-line-notice" role="status">
                    {customLineNotice}
                  </p>
                )}

                {customLineError && (
                  <p className="po-add-line-error" role="alert">
                    {customLineError}
                  </p>
                )}
                <button type="submit" className="btn-primary" disabled={addingLine}>
                  {addingLine ? 'Adding…' : 'Add custom line'}
                </button>
              </form>
            )}
          </div>
        )}

        <table className="items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>SKU</th>
              <th>Quantity Ordered</th>
              <th>Quantity Received</th>
              <th>Unit Cost</th>
              <th>Line Cost</th>
              <th>Expected Shipment Date</th>
              <th>Ordered For</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {order.items.length === 0 ? (
              <tr>
                <td colSpan={10} className="no-data">
                  No line items found
                </td>
              </tr>
            ) : (
              order.items.map((item) => {
                const { itemName, itemSku } = getItemNameAndSku(item);

                return (
                <tr key={item.id} className={item.is_voided ? 'voided-item' : ''}>
                  <td>
                    {itemName}
                    <div>
                      <span className="item-type-badge" data-testid="line-item-type">
                        {getItemTypeLabel(item)}
                      </span>
                    </div>
                    {item.item_type === 'asset' && item.asset_details?.location_name && (
                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.25rem' }}>
                        Location: {item.asset_details.location_name}
                      </div>
                    )}
                    {/* Kit breakdown (op-8n0). Rendered from THIS LINE's
                        payload — never a live kit fetch — so what is shown is
                        what receiving this line will credit. */}
                    {item.is_kit_line && (item.kit_components?.length ?? 0) > 0 && (
                      <div
                        style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.25rem' }}
                        data-testid={`line-kit-breakdown-${item.id}`}
                      >
                        <span className="item-type-badge">kit</span>{' '}
                        Contains:{' '}
                        {(item.kit_components ?? [])
                          .map(
                            (component) =>
                              `${component.component_name} x${component.quantity}`
                          )
                          .join(', ')}
                      </div>
                    )}
                  </td>
                  <td>{itemSku}</td>
                  <td>{item.quantity_ordered}</td>
                  <td>
                    {item.quantity_received}
                    {/* The mismatch, wherever the line is read. Rendered from
                        the server's own derivation (`receipt_state`,
                        `quantity_variance`) rather than recomputed here, so
                        this badge and the API cannot disagree about whether a
                        line came up short. */}
                    {item.has_receipt_variance && (
                      <span
                        className={
                          item.is_over_received
                            ? 'receipt-flag receipt-flag-over'
                            : 'receipt-flag receipt-flag-short'
                        }
                        data-testid={`line-variance-${item.id}`}
                        title={
                          item.closed_short_reason ||
                          (item.is_over_received
                            ? 'More arrived than was ordered'
                            : 'Closed short — the balance was written off')
                        }
                      >
                        {item.quantity_variance > 0
                          ? `+${item.quantity_variance} over`
                          : `${item.quantity_variance} short`}
                      </span>
                    )}
                    {!item.is_settled && item.quantity_received > 0 && (
                      <span
                        className="receipt-flag receipt-flag-pending"
                        data-testid={`line-outstanding-${item.id}`}
                      >
                        {item.quantity_pending} still due
                      </span>
                    )}
                    {item.serials_outstanding > 0 && (
                      <span
                        className="receipt-flag receipt-flag-serials"
                        data-testid={`line-serials-outstanding-${item.id}`}
                        title="These units are in stock but no serial number was recorded for them"
                      >
                        {item.serials_outstanding} without serials
                      </span>
                    )}
                  </td>
                  <td>
                    {/* Truthiness is SAFE here and deliberately kept: DRF
                        serialises this DecimalField as a STRING, so a comped
                        line arrives as "0.00" — truthy — and already displays
                        its own $0.00 with the ordered price beside it. The
                        falsy-zero trap this branch closes elsewhere (op-9m2v)
                        does not bite a string, and the tests below pin that it
                        stays that way if the field is ever parsed to a
                        number. The BACKEND twin of this expression was a real
                        defect, because there the value is a Decimal. */}
                    {item.unit_cost_actual
                      ? formatCurrency(item.unit_cost_actual)
                      : formatCurrency(item.unit_cost_ordered)}
                    {item.unit_cost_actual && (
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                        (ordered: {formatCurrency(item.unit_cost_ordered)})
                      </div>
                    )}
                  </td>
                  <td>
                    {editingCostItemId === item.id ? (
                      <div className="edit-line-cost">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={lineCost}
                          onChange={(e) => setLineCost(e.target.value)}
                          disabled={saving || item.is_voided}
                          className="cost-input"
                          placeholder="Enter total line cost"
                        />
                        <div className="calculated-unit-cost" style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                          Unit cost: {item.quantity_ordered > 0 
                            ? formatCurrency((parseFloat(lineCost || '0') / item.quantity_ordered).toFixed(4))
                            : '—'}
                        </div>
                        <div className="edit-actions">
                          <button
                            onClick={() => handleSaveLineCost(item.id, item)}
                            disabled={saving}
                            className="btn-save"
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEditCost}
                            disabled={saving}
                            className="btn-cancel"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="line-cost-display">
                        <span>
                          {item.actual_cost 
                            ? formatCurrency(item.actual_cost)
                            : formatCurrency(item.estimated_cost)}
                        </span>
                        {!item.is_voided && isAuthenticated && (
                          <button
                            onClick={() => handleEditLineCost(item)}
                            className="btn-edit"
                            title="Edit line cost"
                            style={{ marginLeft: '0.5rem' }}
                          >
                            ✏️
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {editingItemId === item.id ? (
                      <div className="edit-shipment-date">
                        <input
                          type="date"
                          value={shipmentDate}
                          onChange={(e) => setShipmentDate(e.target.value)}
                          disabled={saving || item.is_voided}
                          className="date-input"
                        />
                        <div className="edit-actions">
                          <button
                            onClick={() => handleSaveShipmentDate(item.id)}
                            disabled={saving}
                            className="btn-save"
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={saving}
                            className="btn-cancel"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="shipment-date-display">
                        <span>{formatDate(item.expected_shipment_date)}</span>
                        {!item.is_voided && isAuthenticated && (
                          <button
                            onClick={() => handleEditShipmentDate(item)}
                            className="btn-edit"
                            title="Edit shipment date"
                          >
                            ✏️
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {editingAssociationItemId === item.id ? (
                      <div className="edit-association">
                        <AssociationPickers
                          idPrefix={`line-${item.id}`}
                          workOrderOptions={withCurrentOption(
                            workOrderOptions,
                            lineWorkOrder,
                            workOrderDetailsLabel(item.work_order_details),
                          )}
                          committeeOptions={withCurrentOption(
                            committeeOptions,
                            lineCommittee,
                            item.owning_group_details?.name || '',
                          )}
                          workOrderValue={lineWorkOrder}
                          committeeValue={lineCommittee}
                          onWorkOrderChange={setLineWorkOrder}
                          onCommitteeChange={setLineCommittee}
                          disabled={saving || item.is_voided}
                        />
                        <div className="edit-actions">
                          <button
                            onClick={() => handleSaveAssociation(item.id)}
                            disabled={saving}
                            className="btn-save"
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEditAssociation}
                            disabled={saving}
                            className="btn-cancel"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="association-display">
                        <span className="association-work-order">
                          {workOrderDetailsLabel(item.work_order_details)}
                        </span>
                        <span className="association-committee">
                          {item.owning_group_details?.name || '—'}
                        </span>
                        {!item.is_voided && isAuthenticated && (
                          <button
                            onClick={() => handleEditAssociation(item)}
                            className="btn-edit"
                            title="Edit work order and committee"
                            // The pencil glyph is the button's content, so it
                            // would otherwise be its whole accessible name.
                            aria-label="Edit work order and committee"
                          >
                            ✏️
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {item.is_voided ? (
                      <div className="voided-status">
                        <span className="status-badge status-voided">Voided</span>
                        {item.void_reason && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                            {item.void_reason}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="status-badge status-active">Active</span>
                    )}
                  </td>
                  <td>
                    {isAuthenticated ? (
                      voidingItemId === item.id ? (
                        <div className="void-item-form">
                          <textarea
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            placeholder="Reason for voiding (e.g., item discontinued by supplier)"
                            disabled={saving}
                            rows={3}
                            style={{ width: '100%', marginBottom: '0.5rem' }}
                          />
                          <div className="edit-actions">
                            <button
                              onClick={() => handleVoidItem(item.id)}
                              disabled={saving || !voidReason.trim()}
                              className="btn-void"
                            >
                              {saving ? 'Voiding...' : 'Confirm Void'}
                            </button>
                            <button
                              onClick={() => {
                                setVoidingItemId(null);
                                setVoidReason('');
                              }}
                              disabled={saving}
                              className="btn-cancel"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="item-actions">
                          {!item.is_voided && editingItemId !== item.id && (
                            <>
                              <button
                                onClick={() => handleEditShipmentDate(item)}
                                className="btn-edit-item"
                              >
                                Edit Shipment Date
                              </button>
                              {/* Delete OR void, never both. Which one is
                                  legitimate is the ORDER's question, not the
                                  operator's, so it is answered once by the
                                  API's `can_delete_items` and read here — the
                                  operator is offered the one action that
                                  applies and never has to know the rule.

                                  While the order is still the shop's own
                                  document a mistyped line is a typo, and the
                                  honest record of a typo is no line at all.
                                  Once the supplier holds a copy, erasing it
                                  would be a lie about what was ordered, so
                                  voiding — struck off, on the record, with a
                                  reason — is the only thing offered. */}
                              {order.can_delete_items ? (
                                item.quantity_received === 0 ? (
                                  <button
                                    onClick={() => handleDeleteItem(item)}
                                    disabled={saving}
                                    className="btn-delete-item"
                                    style={{ marginLeft: '0.5rem' }}
                                    data-testid={`delete-line-${item.id}`}
                                  >
                                    Delete Line
                                  </button>
                                ) : (
                                  /* Neither action applies: the server refuses
                                     to delete a line carrying receipts, and
                                     void refuses one too. Offering a button
                                     the server would refuse is worse than
                                     offering none, but an unexplained empty
                                     cell is its own trap — so the row says
                                     what is wrong, in the server's own words. */
                                  <span
                                    className="line-removal-blocked"
                                    data-testid={`line-removal-blocked-${item.id}`}
                                  >
                                    {item.quantity_received} recorded as received — correct the
                                    receipt before this line can be removed.
                                  </span>
                                )
                              ) : (
                                item.quantity_received === 0 && (
                                  <button
                                    onClick={() => setVoidingItemId(item.id)}
                                    className="btn-void-item"
                                    style={{ marginLeft: '0.5rem' }}
                                    data-testid={`void-line-${item.id}`}
                                  >
                                    Void Item
                                  </button>
                                )
                              )}
                              {/* Correcting a close-short made in error. Only
                                  offered on a line that IS closed short: the
                                  receive panel skips settled lines, so without
                                  this the mistake is uncorrectable from the
                                  browser. Labelled "Reopen", never "Undo" —
                                  the close-short stays on the record. */}
                              {item.is_closed_short && (
                                <button
                                  onClick={() => void handleReopenShort(item)}
                                  disabled={saving}
                                  className="btn-reopen-short"
                                  style={{ marginLeft: '0.5rem' }}
                                  data-testid={`reopen-short-${item.id}`}
                                >
                                  Reopen
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )
                    ) : (
                      <span className="view-only-note">View only</span>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
          {order.items.length > 0 && (
            <tfoot>
              <tr className="items-total-row">
                <td colSpan={5} className="items-total-label">
                  Total
                </td>
                <td className="items-total-value" data-testid="po-items-total">
                  {formatCurrency(lineCostTotal.toFixed(2))}
                  {totalsDiffer && (
                    <div className="items-total-estimated">
                      (estimated: {formatCurrency(estimatedLineTotal.toFixed(2))})
                    </div>
                  )}
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          )}
        </table>
      </section>
      </div>
    </WorkspacePage>
  );
};

export default PurchaseOrderPage;

